import { StandardMaterial } from "@babylonjs/core/Materials/standardMaterial";
import { Color3 } from "@babylonjs/core/Maths/math.color";
import { Matrix, Quaternion, Vector3 } from "@babylonjs/core/Maths/math.vector";
import { CreateBox } from "@babylonjs/core/Meshes/Builders/boxBuilder";
import { CreateCylinder } from "@babylonjs/core/Meshes/Builders/cylinderBuilder";
import { CreateSphere } from "@babylonjs/core/Meshes/Builders/sphereBuilder";
import { Mesh } from "@babylonjs/core/Meshes/mesh";
import type { Scene } from "@babylonjs/core/scene";
import "@babylonjs/core/Meshes/thinInstanceMesh";
import { ARENA_ANIM, type ArenaPlayers, type ArenaSport, type ArenaTeam } from "@townsy/contracts";
import type { ArenaSample } from "./timeline";

/**
 * The players and the ball of a stadium game (ADR-0044): low-poly figures in team colours drawn as thin instances, one
 * mesh (one draw call) per body part for everyone on the field, with a run cycle from the interpolated speed and the
 * server's animation codes (action: a kick, a pass, a jump shot or a throw; dunk; layup; shot: a hockey shot wound up
 * and swung through; celebrate; down: tackled or diving; hidden). Hockey players carry sticks; the batter holds a bat.
 *
 * Founder feedback 2026-10-26: the hockey puck is drawn larger than life, with a red arrow floating over it (on top of
 * everything, larger the further the camera is) and a red streak behind it when it flies (passes and shots), so it can
 * be followed from the stands. Only displays the server's positions.
 */
type ColourRole = "shirt" | "shorts" | "skin" | "gear" | "legs" | "arms" | "stick" | "bat";

interface PartSpec {
  name: string;
  size: [number, number, number];
  /** Joint position on the body (metres from the feet, x right, z forward). */
  pivot: [number, number, number];
  /** The part's centre relative to its joint. */
  offset: [number, number, number];
  colour: ColourRole;
  /** Instances per player (2 for legs and arms: left then right). */
  pairs?: boolean;
}

const PARTS: readonly PartSpec[] = [
  { name: "hips", size: [0.4, 0.26, 0.26], pivot: [0, 0.86, 0], offset: [0, 0, 0], colour: "shorts" },
  { name: "torso", size: [0.46, 0.56, 0.28], pivot: [0, 1.17, 0], offset: [0, 0, 0], colour: "shirt" },
  { name: "head", size: [0.24, 0.27, 0.25], pivot: [0, 1.6, 0], offset: [0, 0, 0], colour: "skin" },
  { name: "gear", size: [0.3, 0.12, 0.31], pivot: [0, 1.75, 0], offset: [0, 0, 0.01], colour: "gear" },
  { name: "leg", size: [0.16, 0.74, 0.18], pivot: [0.11, 0.78, 0], offset: [0, -0.37, 0], colour: "legs", pairs: true },
  { name: "arm", size: [0.12, 0.58, 0.13], pivot: [0.3, 1.42, 0], offset: [0, -0.29, 0], colour: "arms", pairs: true },
];
const STICK: PartSpec = { name: "stick", size: [0.05, 0.05, 1.5], pivot: [0.2, 1.0, 0.25], offset: [0, 0, 0.62], colour: "stick" };
const BAT: PartSpec = { name: "bat", size: [0.07, 0.07, 0.86], pivot: [0.22, 1.42, -0.02], offset: [0, 0, 0.4], colour: "bat" };

const SKIN = ["#F2C6A0", "#E0A97E", "#C68A5E", "#9C6643", "#7A4B2E", "#5A3622"];
const HAIR = ["#2B1D14", "#4A2F1E", "#1B1B1B", "#7A4E2A", "#C9A15E", "#3A2A20"];

function hexColor(hex: string): Color3 {
  return Color3.FromHexString(hex);
}

/** Colours of one player's parts. */
function colourOf(role: ColourRole, sport: ArenaSport, team: ArenaTeam, playerRole: string, index: number): string {
  const keeper = playerRole === "G";
  switch (role) {
    case "shirt":
      if (keeper && sport === "soccer") return team.secondary.toLowerCase() === "#f4f4f0" ? "#F2C230" : "#39B54A";
      return team.primary;
    case "shorts":
      return sport === "basketball" ? team.primary : team.secondary;
    case "skin":
      return SKIN[(index * 7 + 3) % SKIN.length]!;
    case "gear":
      if (sport === "football") return team.primary;
      if (sport === "hockey") return team.secondary;
      if (sport === "baseball") return team.primary;
      return HAIR[(index * 5 + 1) % HAIR.length]!;
    case "legs":
      if (sport === "basketball") return SKIN[(index * 7 + 3) % SKIN.length]!;
      if (sport === "baseball") return team.secondary;
      return team.primary;
    case "arms":
      if (sport === "basketball" || sport === "soccer") return SKIN[(index * 7 + 3) % SKIN.length]!;
      return team.primary;
    case "stick":
      return "#3B2A1C";
    case "bat":
      return "#C9A26A";
  }
}

interface Rig {
  phase: number;
  /** Smoothed facing (the interpolated one turns fast; this keeps it calm). */
  facing: number;
  /** The animation code shown and when it started (local ms), for the timed poses (dunk, layup, shot). */
  anim: number;
  animStart: number;
}

/** The hockey puck (metres; a real one is 7.6 cm across: this one is drawn to be seen from the stands). */
export const PUCK_SIZE = { diameter: 0.3, height: 0.07 } as const;
/**
 * The red arrow over the puck: its tip this far above the puck (over the players' heads), drawn at this height
 * (metres) when the camera is near and up to MAX_SCALE times larger far away.
 */
export const PUCK_MARKER = { lift: 2.15, height: 1.0, scaleAt: 18, maxScale: 2.6, colour: "#E3201B" } as const;
/** The streak behind a flying puck: shown above this speed (m/s), covering the last STREAK_MS of its path. */
export const PUCK_STREAK = { minSpeed: 8, ms: 140, maxLength: 5 } as const;

/** Body pose of the timed animations at `t` seconds since they started. */
export interface TimedPose {
  lift: number;
  armL: number;
  armR: number;
  legL: number;
  legR: number;
  /** Stick: pitch at the hands (0.62 = blade on the ice) and yaw across the body (+ wound back, - followed through). */
  stickPitch: number;
  stickYaw: number;
  /** Forward lean of the whole body (radians). */
  lean: number;
}

/**
 * The leaps and the hockey shot by time: a dunk goes high with both arms over the rim; a layup is a one-handed running
 * leap; a hockey shot winds the stick back for 0.24 s, swings it through and holds the follow-through; a hockey pass
 * sweeps the stick across.
 */
export function timedPose(anim: number, sport: ArenaSport, t: number): TimedPose | null {
  const s = Math.max(0, t);
  if (anim === ARENA_ANIM.dunk) {
    const up = Math.sin(Math.min(1, s / 0.62) * Math.PI);
    return { lift: up * 0.95, armL: -3.0, armR: -3.0, legL: 0.55, legR: 0.35, stickPitch: 0.62, stickYaw: 0, lean: 0 };
  }
  if (anim === ARENA_ANIM.layup) {
    const up = Math.sin(Math.min(1, s / 0.5) * Math.PI);
    return { lift: up * 0.55, armL: -0.5, armR: -2.9, legL: 1.1, legR: -0.25, stickPitch: 0.62, stickYaw: 0, lean: 0 };
  }
  if (anim === ARENA_ANIM.shot) {
    const wind = 0.24;
    if (s < wind) {
      const k = s / wind;
      return { lift: 0, armL: -0.9 * k, armR: -1.2 * k, legL: 0.25, legR: -0.25, stickPitch: 0.62 - 1.1 * k, stickYaw: 1.3 * k, lean: 0.12 * k };
    }
    const k = Math.min(1, (s - wind) / 0.16);
    return { lift: 0, armL: -0.9 + 0.2 * k, armR: -1.2 - 0.5 * k, legL: 0.25, legR: -0.4, stickPitch: -0.48 + 0.75 * k, stickYaw: 1.3 - 2.9 * k, lean: 0.12 + 0.18 * k };
  }
  if (anim === ARENA_ANIM.action && sport === "hockey") {
    const k = Math.min(1, s / 0.22);
    return { lift: 0, armL: 0, armR: -1.3, legL: 0, legR: 0, stickPitch: 0.62, stickYaw: 0.8 - 1.8 * k, lean: 0.08 };
  }
  return null;
}

const TMP = { a: new Matrix(), b: new Matrix(), c: new Matrix(), d: new Matrix(), root: new Matrix(), pose: new Matrix() };
const V = { s: new Vector3(), t: new Vector3() };
const Q = new Quaternion();

/** world = scale(size) * translate(offset) * rotate(joint) * translate(pivot) * pose * root. */
function partMatrix(spec: PartSpec, side: number, jointX: number, jointY: number, pose: Matrix, root: Matrix, out: Float32Array, at: number, scale = 1): void {
  V.s.set(spec.size[0] * scale, spec.size[1] * scale, spec.size[2] * scale);
  V.t.set(spec.offset[0], spec.offset[1], spec.offset[2]);
  Q.set(0, 0, 0, 1);
  Matrix.ComposeToRef(V.s, Q, V.t, TMP.a);
  Quaternion.RotationYawPitchRollToRef(jointY, jointX, 0, Q);
  V.s.set(1, 1, 1);
  V.t.set(spec.pivot[0] * side, spec.pivot[1], spec.pivot[2]);
  Matrix.ComposeToRef(V.s, Q, V.t, TMP.b);
  TMP.a.multiplyToRef(TMP.b, TMP.c);
  TMP.c.multiplyToRef(pose, TMP.d);
  TMP.d.multiplyToRef(root, TMP.c);
  TMP.c.copyToArray(out, at);
}

function zeroMatrix(out: Float32Array, at: number): void {
  for (let k = 0; k < 16; k++) out[at + k] = 0;
}

export interface PlayersHandle {
  meshes: Mesh[];
  /** New teams (a new game): recolour everyone. */
  setTeams(home: ArenaTeam, away: ArenaTeam): void;
  /** Draw the interpolated sample; `batter` = index of the player holding the bat (baseball), or -1. */
  update(sample: ArenaSample | null, dtSeconds: number, nowMs: number, batter: number): void;
  /** Positions of the players (diagnostics). */
  positions(): Array<{ x: number; z: number; anim: number }>;
  ballPosition(): { x: number; y: number; z: number } | null;
  /** Hockey: the arrow over the puck (tip position and scale) and the streak's length, or null when not shown. */
  puckMarker(): { x: number; y: number; z: number; scale: number } | null;
  puckStreak(): number | null;
  dispose(): void;
}

export function buildPlayers(scene: Scene, sport: ArenaSport, roster: ArenaPlayers, home: ArenaTeam, away: ArenaTeam): PlayersHandle {
  const n = roster.team.length;
  const mat = new StandardMaterial("arenaPlayersMat", scene);
  mat.diffuseColor = Color3.White();
  mat.specularColor = new Color3(0.08, 0.08, 0.08);
  mat.ambientColor = Color3.White();
  const specs: PartSpec[] = [...PARTS];
  if (sport === "hockey") specs.push(STICK);
  if (sport === "baseball") specs.push(BAT);
  const parts = specs.map((spec) => {
    const m = CreateBox(`arenaPlayer_${spec.name}`, { size: 1 }, scene);
    m.material = mat;
    m.isPickable = false;
    m.receiveShadows = false;
    m.alwaysSelectAsActiveMesh = true;
    const count = spec === BAT ? 1 : n * (spec.pairs ? 2 : 1);
    const matrices = new Float32Array(count * 16);
    m.thinInstanceSetBuffer("matrix", matrices, 16, false);
    const colors = new Float32Array(count * 4);
    m.thinInstanceSetBuffer("color", colors, 4, false);
    return { spec, mesh: m, matrices, colors, count };
  });
  const rigs: Rig[] = Array.from({ length: n }, (_, i) => ({ phase: i * 0.9, facing: 0, anim: ARENA_ANIM.idle, animStart: 0 }));
  let last: ArenaSample | null = null;

  const recolour = (h: ArenaTeam, a: ArenaTeam) => {
    for (const p of parts) {
      if (p.spec === BAT) {
        const c = hexColor(colourOf("bat", sport, h, "", 0));
        p.colors.set([c.r, c.g, c.b, 1], 0);
      } else {
        for (let i = 0; i < n; i++) {
          const team = roster.team[i] === 0 ? h : a;
          const c = hexColor(colourOf(p.spec.colour, sport, team, roster.role[i] ?? "", i));
          const per = p.spec.pairs ? 2 : 1;
          for (let k = 0; k < per; k++) p.colors.set([c.r, c.g, c.b, 1], (i * per + k) * 4);
        }
      }
      p.mesh.thinInstanceBufferUpdated("color");
    }
  };
  recolour(home, away);

  // The ball (or puck) and its shadow.
  const ballMat = new StandardMaterial("arenaBallMat", scene);
  ballMat.specularColor = new Color3(0.2, 0.2, 0.2);
  let ball: Mesh;
  switch (sport) {
    case "football":
      ball = CreateSphere("arenaBall", { diameterX: 0.22, diameterY: 0.22, diameterZ: 0.36, segments: 8 }, scene);
      ballMat.diffuseColor = hexColor("#7A3E1B");
      break;
    case "soccer":
      ball = CreateSphere("arenaBall", { diameter: 0.34, segments: 8 }, scene);
      ballMat.diffuseColor = hexColor("#F8F8F4");
      break;
    case "baseball":
      // Larger than life so it can be followed from the stands.
      ball = CreateSphere("arenaBall", { diameter: 0.22, segments: 6 }, scene);
      ballMat.diffuseColor = hexColor("#FFFFFF");
      ballMat.emissiveColor = new Color3(0.35, 0.35, 0.35);
      break;
    case "basketball":
      ball = CreateSphere("arenaBall", { diameter: 0.3, segments: 8 }, scene);
      ballMat.diffuseColor = hexColor("#E06A1B");
      break;
    case "hockey":
      // Larger than life (a real puck is 7.6 cm), jet black on the white ice.
      ball = CreateCylinder("arenaBall", { diameter: PUCK_SIZE.diameter, height: PUCK_SIZE.height, tessellation: 16 }, scene);
      ballMat.diffuseColor = hexColor("#050505");
      ballMat.specularColor = new Color3(0.05, 0.05, 0.05);
      break;
  }
  ball.material = ballMat;
  ball.isPickable = false;
  const shadowMat = new StandardMaterial("arenaBallShadowMat", scene);
  shadowMat.diffuseColor = Color3.Black();
  shadowMat.specularColor = Color3.Black();
  shadowMat.alpha = 0.32;
  shadowMat.disableLighting = true;
  const shadow = CreateCylinder("arenaBallShadow", { diameter: sport === "hockey" ? PUCK_SIZE.diameter + 0.06 : 0.42, height: 0.004, tessellation: 12 }, scene);
  shadow.material = shadowMat;
  shadow.isPickable = false;
  ball.setEnabled(false);
  shadow.setEnabled(false);
  let spin = 0;

  // Hockey: a red arrow pointing down at the puck (drawn over everything) and a streak behind it when it flies.
  let marker: Mesh | null = null;
  let streak: Mesh | null = null;
  let markerMat: StandardMaterial | null = null;
  let streakMat: StandardMaterial | null = null;
  let markerScale = 1;
  let streakLength: number | null = null;
  const trail: Array<{ t: number; x: number; y: number; z: number }> = [];
  if (sport === "hockey") {
    markerMat = new StandardMaterial("arenaPuckMarkerMat", scene);
    const red = hexColor(PUCK_MARKER.colour);
    markerMat.diffuseColor = red;
    markerMat.emissiveColor = red;
    markerMat.specularColor = Color3.Black();
    markerMat.disableLighting = true;
    const h = PUCK_MARKER.height;
    // The tip at the origin, the head (60%) and a shaft above it.
    const head = CreateCylinder("arenaPuckMarkerHead", { diameterTop: 0.62 * h, diameterBottom: 0, height: 0.6 * h, tessellation: 16 }, scene);
    head.bakeTransformIntoVertices(Matrix.Translation(0, 0.3 * h, 0));
    const shaft = CreateCylinder("arenaPuckMarkerShaft", { diameter: 0.24 * h, height: 0.42 * h, tessellation: 12 }, scene);
    shaft.bakeTransformIntoVertices(Matrix.Translation(0, 0.6 * h + 0.21 * h, 0));
    marker = Mesh.MergeMeshes([head, shaft], true, true) ?? head;
    marker.name = "arenaPuckMarker";
    marker.material = markerMat;
    marker.isPickable = false;
    marker.alwaysSelectAsActiveMesh = true;
    // Over everything (players, boards, glass): the puck can always be found.
    marker.renderingGroupId = 1;
    marker.setEnabled(false);
    streakMat = new StandardMaterial("arenaPuckStreakMat", scene);
    streakMat.diffuseColor = red;
    streakMat.emissiveColor = red;
    streakMat.specularColor = Color3.Black();
    streakMat.disableLighting = true;
    streakMat.alpha = 0.7;
    streak = CreateBox("arenaPuckStreak", { width: PUCK_SIZE.diameter * 0.85, height: 0.1, depth: 1 }, scene);
    streak.material = streakMat;
    streak.isPickable = false;
    streak.alwaysSelectAsActiveMesh = true;
    streak.setEnabled(false);
  }

  const placeMarker = (b: readonly [number, number, number], nowMs: number) => {
    if (!marker || !streak) return;
    const cam = scene.activeCamera;
    const far = cam ? Math.hypot(cam.globalPosition.x - b[0], cam.globalPosition.y - b[1], cam.globalPosition.z - b[2]) : PUCK_MARKER.scaleAt;
    markerScale = Math.min(PUCK_MARKER.maxScale, Math.max(1, far / PUCK_MARKER.scaleAt));
    const bob = Math.sin(nowMs / 180) * 0.08 * markerScale;
    marker.position.set(b[0], Math.max(0.03, b[1]) + PUCK_MARKER.lift + bob, b[2]);
    marker.scaling.setAll(markerScale);
    marker.setEnabled(true);
    // The streak: from where the puck was STREAK_MS ago to where it is, when it is flying fast.
    trail.push({ t: nowMs, x: b[0], y: Math.max(0.03, b[1]), z: b[2] });
    while (trail.length > 2 && trail[0]!.t < nowMs - PUCK_STREAK.ms) trail.shift();
    const old = trail[0]!;
    const dx = b[0] - old.x;
    const dy = Math.max(0.03, b[1]) - old.y;
    const dz = b[2] - old.z;
    const len = Math.hypot(dx, dy, dz);
    const secs = Math.max(0.001, (nowMs - old.t) / 1000);
    if (len / secs >= PUCK_STREAK.minSpeed && nowMs - old.t >= 30) {
      const l = Math.min(PUCK_STREAK.maxLength, len);
      streak.position.set(b[0] - (dx / len) * (l / 2), Math.max(0.03, b[1]) - (dy / len) * (l / 2), b[2] - (dz / len) * (l / 2));
      streak.rotation.set(-Math.atan2(dy, Math.hypot(dx, dz)), Math.atan2(dx, dz), 0);
      streak.scaling.set(1, 1, l);
      streak.setEnabled(true);
      streakLength = l;
    } else {
      streak.setEnabled(false);
      streakLength = null;
    }
  };
  const hideMarker = () => {
    marker?.setEnabled(false);
    streak?.setEnabled(false);
    streakLength = null;
    trail.length = 0;
  };

  return {
    meshes: [...parts.map((p) => p.mesh), ball],
    setTeams: recolour,
    update(sample, dt, nowMs, batter) {
      last = sample;
      if (!sample) {
        for (const p of parts) p.mesh.setEnabled(false);
        ball.setEnabled(false);
        shadow.setEnabled(false);
        hideMarker();
        return;
      }
      for (const p of parts) p.mesh.setEnabled(true);
      const t = nowMs / 1000;
      for (let i = 0; i < n; i++) {
        const x = sample.pos[i * 4]!;
        const z = sample.pos[i * 4 + 1]!;
        const facing = sample.pos[i * 4 + 2]!;
        const anim = sample.pos[i * 4 + 3]!;
        const speed = sample.speed[i] ?? 0;
        const rig = rigs[i]!;
        rig.facing = facing;
        if (anim !== rig.anim) {
          rig.anim = anim;
          rig.animStart = nowMs;
        }
        const running = anim === ARENA_ANIM.run || speed > 0.6;
        if (running) rig.phase += dt * Math.min(14, 4 + speed * 1.4);
        const swing = running ? Math.sin(rig.phase) * Math.min(0.85, 0.25 + speed * 0.09) : 0;
        let lift = 0;
        let fall = 0;
        let legL = swing;
        let legR = -swing;
        let armL = -swing * 0.9;
        let armR = swing * 0.9;
        let stickYaw = 0;
        let stickPitch = 0.62;
        const timed = timedPose(anim, sport, (nowMs - rig.animStart) / 1000);
        if (timed) {
          lift = timed.lift;
          legL = timed.legL;
          legR = timed.legR;
          armL = timed.armL;
          armR = timed.armR;
          stickYaw = timed.stickYaw;
          stickPitch = timed.stickPitch;
          fall = timed.lean;
        } else if (anim === ARENA_ANIM.action) {
          if (sport === "soccer" || (sport === "football" && roster.role[i] !== "QB")) legR = -1.1;
          else if (sport === "basketball") armL = armR = -2.75;
          else {
            armR = -1.6;
            stickYaw = -0.9;
          }
        } else if (anim === ARENA_ANIM.celebrate) {
          armL = armR = -2.9;
          lift = Math.abs(Math.sin(t * 7 + i)) * 0.28;
        } else if (anim === ARENA_ANIM.down) {
          fall = Math.PI / 2;
          lift = 0.16;
        }
        const keeperBulk = sport === "hockey" && roster.role[i] === "G" ? 1.25 : 1;
        Matrix.RotationYawPitchRollToRef(facing, 0, 0, TMP.pose);
        TMP.root.copyFrom(TMP.pose);
        TMP.root.setTranslationFromFloats(x, lift, z);
        Matrix.RotationXToRef(fall, TMP.pose);
        const hidden = anim === ARENA_ANIM.hidden;
        for (const p of parts) {
          if (p.spec === BAT) continue;
          const per = p.spec.pairs ? 2 : 1;
          for (let k = 0; k < per; k++) {
            const at = (i * per + k) * 16;
            if (hidden) {
              zeroMatrix(p.matrices, at);
              continue;
            }
            const side = p.spec.pairs ? (k === 0 ? -1 : 1) : 1;
            let jx = 0;
            let jy = 0;
            if (p.spec.name === "leg") jx = k === 0 ? legL : legR;
            else if (p.spec.name === "arm") jx = k === 0 ? armL : armR;
            else if (p.spec === STICK) {
              jx = stickPitch;
              jy = stickYaw;
            }
            const bulk = p.spec.name === "torso" || p.spec.name === "leg" ? keeperBulk : 1;
            partMatrix(p.spec, side, jx, jy, TMP.pose, TMP.root, p.matrices, at, bulk);
          }
        }
      }
      // The bat goes with the batter.
      const bat = parts.find((p) => p.spec === BAT);
      if (bat) {
        if (batter >= 0 && batter < n) {
          const x = sample.pos[batter * 4]!;
          const z = sample.pos[batter * 4 + 1]!;
          const facing = sample.pos[batter * 4 + 2]!;
          const swinging = sample.pos[batter * 4 + 3] === ARENA_ANIM.action;
          Matrix.RotationYawPitchRollToRef(facing, 0, 0, TMP.root);
          TMP.root.setTranslationFromFloats(x, 0, z);
          Matrix.IdentityToRef(TMP.pose);
          partMatrix(BAT, 1, swinging ? 0.1 : -1.1, swinging ? -1.6 : 0.3, TMP.pose, TMP.root, bat.matrices, 0);
        } else zeroMatrix(bat.matrices, 0);
      }
      for (const p of parts) p.mesh.thinInstanceBufferUpdated("matrix");
      const b = sample.ball;
      ball.setEnabled(b !== null);
      shadow.setEnabled(b !== null);
      if (b) {
        spin += dt * 9;
        ball.position.set(b[0], Math.max(sport === "hockey" ? 0.03 : 0.11, b[1]), b[2]);
        if (sport !== "hockey") ball.rotation.set(spin, spin * 0.6, 0);
        shadow.position.set(b[0], 0.03, b[2]);
        const s = Math.max(0.35, 1 - b[1] / 14);
        shadow.scaling.set(s, 1, s);
        placeMarker(b, nowMs);
      } else hideMarker();
    },
    positions() {
      if (!last) return [];
      const out: Array<{ x: number; z: number; anim: number }> = [];
      for (let i = 0; i < n; i++) out.push({ x: last.pos[i * 4]!, z: last.pos[i * 4 + 1]!, anim: last.pos[i * 4 + 3]! });
      return out;
    },
    ballPosition() {
      return ball.isEnabled() ? { x: ball.position.x, y: ball.position.y, z: ball.position.z } : null;
    },
    puckMarker() {
      return marker && marker.isEnabled() ? { x: marker.position.x, y: marker.position.y, z: marker.position.z, scale: markerScale } : null;
    },
    puckStreak() {
      return streak && streak.isEnabled() ? streakLength : null;
    },
    dispose() {
      for (const p of parts) p.mesh.dispose();
      ball.dispose();
      shadow.dispose();
      marker?.dispose();
      streak?.dispose();
      mat.dispose();
      ballMat.dispose();
      shadowMat.dispose();
      markerMat?.dispose();
      streakMat?.dispose();
    },
  };
}
